import { z } from 'zod';
/**
 * The form configuration JSON document is the contract shared by every deployable:
 * the Form Designer writes it, the backend stores/serves it, the storefront script
 * consumes it. Because this schema is imported everywhere, the Designer cannot emit
 * anything the storefront cannot render.
 *
 * Forward-compatibility rules baked into this file:
 *  - New features become NEW top-level keys, never new columns or a reshaped payload.
 *  - The root object and the `_future` block use `.passthrough()` so an older renderer
 *    tolerates a config that carries keys it does not yet understand.
 *  - `fields` is a data-driven array the renderer loops over; adding field types or
 *    icons is an enum extension, not a structural change.
 */
/**
 * A CSS color string. Permissive on purpose: the designer's color control offers a hex
 * swatch but its text field accepts any valid CSS color (hex, rgb/rgba, hsl, named,
 * `transparent`). One color type is used for every color field so the UI stays uniform.
 */
export const cssColor = z.string().min(1).max(64);
export const formMode = z.enum(['popup', 'embedded']);
/**
 * Where the COD form is shown on the storefront. `disabled` hides it everywhere; the others
 * scope it to the cart page, product pages, or both. A store-level setting, mirrored into the
 * served config so the storefront bundle knows where it may render.
 */
export const formVisibility = z.enum(['disabled', 'cart', 'product', 'both']);
/**
 * What the product-page form loads. `product_only`: just the product being viewed (cart contents
 * are ignored). `product_and_cart`: the whole cart plus the current product (added when it isn't
 * already in the cart), submitted as one multi-line order. A Settings toggle; only affects the
 * product surface (the cart-drawer form is always the cart).
 */
/**
 * 🚨 Which of the two dual-shipped storefront bundles owns this store — the double-render
 * kill-switch (`MIGRATION.md` §7.6).
 *
 * The platform does **not** guarantee that only one of our Scripts executes. Verbatim from
 * `/nube-sdk/after-migration`: _"the moment a behavior is migrated to the SDK you must remove that
 * behavior from the legacy script. Otherwise the same logic runs twice — leading to duplicated UI,
 * conflicting events, or double-counted actions."_ On an SDK-enabled store both bundles boot, so
 * the COD button renders **twice** unless one stands down.
 *
 * It has to be the LEGACY bundle that stands down: the SDK app runs in a Web Worker and cannot see
 * whether the other script is on the page. So this field is served to both, and the legacy bundle
 * returns immediately from `init()` when it reads `'nubesdk'`.
 *
 * Server-derived, never merchant-authored — see `resolveStorefrontRuntime`. It is also a literal
 * item on Nuvemshop's pre-production checklist ("Legacy/transition script no longer runs any
 * behavior already migrated to the SDK"), so homologation looks for it.
 */
export const storefrontRuntime = z.enum(['legacy', 'nubesdk']);
export const cartMode = z.enum(['product_only', 'product_and_cart']);
/**
 * Icon identifiers are an open-ended catalog. The renderer maps each to an inline SVG
 * and falls back to "none" for unknown values, so adding icons never breaks old configs.
 */
export const buttonIcon = z.enum(['bag', 'cart', 'none']);
export const fieldIcon = z.enum(['user', 'phone', 'pin', 'hash', 'at', 'note', 'mail', 'none']);
/**
 * Field types. The input types (`text`/`tel`/`email`/`textarea`/`number`/`dropdown`/
 * `single_choice`/`checkbox`/`date`) collect a value into the order payload. The remaining
 * "block" types render a non-input section at this position in the form instead of an input:
 * the singleton sections (`quantity_offers`, `order_summary`, `totals_summary`,
 * `shipping_method`, `discount_code`) read their rich config from a matching
 * top-level key, while the custom block-action types (`custom_text`, `custom_image`,
 * `whatsapp_button`, `link_button`) carry their config inline on the field entry. Modeling
 * everything as fields means each one reorders and shows/hides exactly like every other field.
 *
 * 🚨 **`address_heading` is retired (2026-08-19).** It was a singleton block whose entire config
 * was one string, rendered as centred bold text — which is precisely what `custom_text` already
 * does, with alignment, size, weight and colour the merchant controls instead of a fixed look.
 * The default config now ships a `custom_text` entry carrying the address wording, and
 * `migrateAddressHeadingField` (`validate.ts`) converts stored entries — keeping the merchant's
 * own text and its position. Do not reintroduce the type: two blocks that render one line of
 * centred text is the kind of duplication that makes the Designer harder to learn, and the
 * removal is only cheap while the app is unlaunched.
 */
export const fieldType = z.enum([
    'text',
    'tel',
    'email',
    'textarea',
    'number',
    'quantity_offers',
    'order_summary',
    'totals_summary',
    'shipping_method',
    'discount_code',
    // Singleton form-chrome blocks (config in a matching top-level key): the submit/CTA button and
    // the two consent lines. `submit` collects nothing; `terms`/`newsletter` emit a consent boolean
    // via their own path (not the readers map).
    'submit',
    'terms',
    'newsletter',
    // Custom block-action types (no collected value; config lives inline on the field).
    'custom_text',
    'custom_image',
    'whatsapp_button',
    'link_button',
    // Custom input types (collect a value via the input path).
    'dropdown',
    'single_choice',
    'checkbox',
    'date',
]);
/**
 * Field types that render a non-input block (no value collected, skipped on submit). The
 * renderer and designer branch on this set instead of checking each type by hand. The custom
 * input types (`dropdown`/`single_choice`/`checkbox`/`date`) are NOT here — they collect values.
 */
export const blockFieldTypes = [
    'quantity_offers',
    'order_summary',
    'totals_summary',
    'shipping_method',
    'discount_code',
    'submit',
    'terms',
    'newsletter',
    'custom_text',
    'custom_image',
    'whatsapp_button',
    'link_button',
];
export function isBlockField(type) {
    return blockFieldTypes.includes(type);
}
export const buttonPlacement = z.enum(['after_buy_button', 'before_buy_button']);
/**
 * The length bounds the Designer offers on `min_length` / `max_length`.
 *
 * Lives here rather than in the admin so the control's range and the contract it writes into
 * cannot drift: `FIELD_LENGTH_MAX` is the schema's own ceiling, and both field editors clamp to
 * this pair. It came down from 1000, which let a merchant cap a first name at four times the
 * length of any address we would accept.
 *
 * 🚨 `FIELD_LENGTH_MIN` is **0**, and 0 means "no check" rather than "at least nothing" — both
 * renderers gate on `field.min_length > 0` before comparing anything. It is also the shipped
 * default on every field.
 *
 * The Designer used to floor these controls at 1, which quietly made the setting one-way: a
 * merchant who typed a bound could never clear it again, because the control refused to go back to
 * the value the field started on. The schema always accepted 0 — it has to, or every config ever
 * saved would fail validation — so the floor was only ever a UI restriction, and it cost the
 * merchant the ability to undo their own edit.
 */
export const FIELD_LENGTH_MIN = 0;
export const FIELD_LENGTH_MAX = 250;
/**
 * Horizontal alignment for text content. Declared up here rather than with the custom-field
 * blocks below because the form header uses it too, and a `z.object` evaluates its shape
 * eagerly — referencing it from `headerConfig` before its declaration is a TDZ crash, not a
 * type error.
 */
export const textAlign = z.enum(['left', 'center', 'right']);
/** Form header: the title row at the top of the form, with an optional home icon. */
export const headerConfig = z.object({
    title: z.string().min(1).max(120),
    show_icon: z.boolean(),
    // Whether the header renders on the storefront. `.default(true)` backfills configs saved
    // before the header could be hidden, preserving the historical always-visible behavior.
    enabled: z.boolean().default(true),
    // Where the title (and its icon) sit in the header band. `.default('left')` backfills configs
    // saved before this existed — and left is what they rendered as, since the band was a flex row
    // whose single child fell to the start.
    align: textAlign.default('left'),
});
export const defaultHeader = {
    title: 'Pago contra entrega',
    show_icon: true,
    enabled: true,
    align: 'left',
};
/** Customizable COD button (popup mode only). */
export const buttonConfig = z.object({
    text: z.string().min(1).max(120),
    bg_color: cssColor,
    text_color: cssColor,
    border_radius: z.number().int().min(0).max(100),
    border_width: z.number().int().min(0).max(20),
    border_color: cssColor,
    icon: buttonIcon,
    placement: buttonPlacement,
});
export const appearanceConfig = z.object({
    border_radius: z.number().int().min(0).max(100),
    /**
     * 🚨 RETIRED as a setting (2026-08-17). Nothing reads it.
     *
     * Its Designer picker was removed because it looked inert — and it looked inert because each of
     * its three uses was live on only ONE of the two bundles, so whichever a merchant tested, most
     * of it did nothing. All three now take the colour the *other* bundle already applied, which
     * also closes three dual-ship divergences:
     *
     *  - consent checkbox tick → `accent_color` (the selection colour the radios already use)
     *  - thank-you ✔          → `text_color` (what the legacy's `.cod-check` inherited)
     *  - thank-you handoff btn → the submit button's own colours (what NubeSDK already used)
     *
     * The FIELD stays: it is required (no default), so dropping it would fail validation for every
     * config ever saved, for no benefit.
     */
    primary_color: cssColor,
    bg_color: cssColor,
    text_color: cssColor,
    accent_color: cssColor,
});
/* ── Custom field building blocks ──────────────────────────────────────────────
 * Custom fields (merchant-added via the designer's "Add custom field" modal) carry their
 * per-instance config inline on the field entry, because — unlike the singleton sections —
 * there can be many of each. The block-action types render styled content; the input types
 * collect a value. All groups are optional on `fieldConfig`; only the matching type sets one.
 */
/* `textAlign` is declared above `headerConfig`, which shares it — see the note there. */
export const fontWeight = z.enum(['normal', 'bold']);
export const buttonAnimation = z.enum(['none', 'pulse', 'shake', 'bounce', 'tada']);
/** "Title or text": free text (supports {order_total}, {product_name}) with typography. */
export const textBlockConfig = z.object({
    content: z.string().max(2000).default(''),
    align: textAlign.default('left'),
    font_size: z.number().int().min(8).max(80).default(16),
    font_weight: fontWeight.default('normal'),
    text_color: cssColor.default('#000000'),
});
/** "Image or GIF": a hosted image rendered at a percentage of the form width. */
export const imageBlockConfig = z.object({
    url: z.string().max(2048).default(''),
    /** Width as a percentage of the form (10–100). */
    image_size: z.number().int().min(10).max(100).default(100),
});
/** Shared visual styling for the action buttons (WhatsApp, link). */
export const actionButtonStyle = z.object({
    bg_color: cssColor.default('#25D366'),
    text_color: cssColor.default('#ffffff'),
    font_size: z.number().int().min(8).max(40).default(16),
    border_radius: z.number().int().min(0).max(100).default(8),
    border_width: z.number().int().min(0).max(20).default(0),
    border_color: cssColor.default('#000000'),
    shadow: z.number().int().min(0).max(40).default(0),
    animation: buttonAnimation.default('none'),
});
export const defaultActionButtonStyle = {
    bg_color: '#25D366',
    text_color: '#ffffff',
    font_size: 16,
    border_radius: 8,
    border_width: 0,
    border_color: '#000000',
    shadow: 0,
    animation: 'none',
};
/**
 * "WhatsApp button": opens a wa.me chat with an interpolated, pre-filled message. When
 * `create_order` is set, clicking also validates + submits the COD order before opening chat.
 * `text` supports {order_total},{order_subtotal}; `message` supports {page_url},
 * {products_summary_with_quantity},{order_total},{full_name},{phone},{email},{full_address},
 * {order_note}.
 */
export const whatsappConfig = z.object({
    text: z.string().max(160).default('WhatsApp'),
    subtitle: z.string().max(160).default(''),
    phone: z.string().max(32).default(''),
    create_order: z.boolean().default(false),
    message: z.string().max(2000).default(''),
    style: actionButtonStyle.default(defaultActionButtonStyle),
});
/** "Link button": opens a URL in a new tab. */
export const linkButtonConfig = z.object({
    text: z.string().max(160).default(''),
    subtitle: z.string().max(160).default(''),
    url: z.string().max(2048).default(''),
    style: actionButtonStyle.default(defaultActionButtonStyle),
});
/**
 * Post-order "thank you" behavior. After a successful COD order the storefront either shows a
 * custom HTML page, redirects to a URL, or opens a WhatsApp chat — instead of the built-in
 * inline confirmation. `page_html` is raw HTML the merchant authors; `whatsapp_message`
 * supports {order_number},{order_total},{full_name},{phone} placeholders. Disabled by default
 * so stores saved before this existed keep the built-in confirmation panel.
 */
export const thankYouMode = z.enum(['page', 'url', 'whatsapp']);
export const thankYouConfig = z.object({
    enabled: z.boolean().default(false),
    mode: thankYouMode.default('page'),
    page_html: z.string().max(20000).default(''),
    /**
     * The merchant's own page, kept while they use one of the admin's ready-made designs.
     *
     * 🚨 Storage only — **no storefront reads this**. `page_html` is what buyers get; this is the
     * admin's "Your own HTML" tab remembering its content across an inserted template, which
     * overwrites `page_html` by design. Without a field of its own, a merchant who hand-wrote a page,
     * then tried a design and saved, lost their page permanently: there was one slot and the template
     * was in it.
     *
     * Same 20k cap as `page_html`, since it is the same document at a different moment.
     */
    custom_page_html: z.string().max(20000).default(''),
    redirect_url: z.string().max(2048).default(''),
    whatsapp_phone: z.string().max(32).default(''),
    whatsapp_message: z.string().max(2000).default(''),
});
export const defaultThankYou = {
    enabled: false,
    mode: 'page',
    page_html: '',
    custom_page_html: '',
    redirect_url: '',
    whatsapp_phone: '',
    whatsapp_message: 'Hola! Mi pedido {order_number} por un total de {order_total} fue registrado.',
};
/**
 * A single data-driven form field. `key` is stable and maps to the order payload;
 * everything else is merchant-editable. The renderer loops over the ordered array, so
 * its order IS the on-screen order (the designer reorders by editing this array). New
 * optional attributes use `.default()` so configs saved before they existed backfill.
 */
export const fieldConfig = z.object({
    key: z.string().min(1).max(64),
    label: z.string().min(1).max(120),
    /** Input placeholder. Empty falls back to the label at render time. */
    placeholder: z.string().max(120).default(''),
    type: fieldType,
    required: z.boolean(),
    icon: fieldIcon,
    /** Whether to render the field's leading icon. */
    show_icon: z.boolean().default(true),
    enabled: z.boolean(),
    /**
     * Value length bounds, in the range the Designer offers — see {@link FIELD_LENGTH_MIN}.
     *
     * 🚨 `0` stays valid and still means "no check". It is the shipped default on every field, so
     * raising the floor here would fail validation for every config ever saved.
     */
    min_length: z.number().int().min(0).max(FIELD_LENGTH_MAX).default(0),
    /** Maximum value length. 0 disables the check (no cap) — see `min_length`. */
    max_length: z.number().int().min(0).max(FIELD_LENGTH_MAX).default(0),
    /** Custom "invalid value" message. Empty falls back to messages.generic_invalid. */
    error_text: z.string().max(240).default(''),
    /** Merchant-added field: deletable, edited via the extended custom editor. */
    custom: z.boolean().default(false),
    /** Standard field key this value feeds into (e.g. 'phone'). '' = Nothing (custom data). */
    connect_to: z.string().max(64).default(''),
    /** Option values for dropdown / single_choice. */
    options: z.array(z.string().max(120)).default([]),
    /** Text input: restrict input to digits. */
    allow_only_numbers: z.boolean().default(false),
    /** Text input: non-editable leading text shown inside the field. */
    prefix_text: z.string().max(40).default(''),
    /** Per-type custom config; only the matching field type sets one. */
    text_block: textBlockConfig.optional(),
    image: imageBlockConfig.optional(),
    whatsapp: whatsappConfig.optional(),
    link: linkButtonConfig.optional(),
});
export const borderMode = z.enum(['none', 'border']);
export const borderPosition = z.enum(['all', 'top', 'right', 'bottom', 'left']);
/** Visual styling applied to every input/textarea in the form. */
export const fieldsStyleConfig = z.object({
    bg_color: cssColor,
    text_color: cssColor,
    /** Placeholder text color for every input/textarea. Fields with their own placeholder picker
     *  (e.g. the discount input) override this via a more specific rule. `.default()` backfills
     *  configs saved before this key existed. */
    placeholder_color: cssColor.default('rgba(0,0,0,.45)'),
    border_color: cssColor,
    border_mode: borderMode,
    border_position: borderPosition,
    /** Thickness of the edge(s) `border_position` draws, in px. Bounded like every other border
     *  width in the config (`button`, `actionButtonStyle`). Floor of 1, not 0: `border_mode: 'none'`
     *  already expresses "no border", and a second way to say it would let a merchant set a border
     *  that is on but invisible. `.default(1)` is the width every config rendered at before this
     *  existed, so backfilled configs look identical. */
    border_width: z.number().int().min(1).max(20).default(1),
    border_radius: z.number().int().min(0).max(100),
    shadow: z.number().int().min(0).max(40),
    sizing: z.number().int().min(0).max(40),
    /** Hide the caption above every input (the placeholder, which falls back to the label, still
     *  identifies the field). `.default(false)` keeps labels visible for configs saved before this. */
    hide_labels: z.boolean().default(false),
});
export const defaultFieldsStyle = {
    bg_color: '#f6f7fb',
    text_color: '#000000',
    placeholder_color: 'rgba(0,0,0,.45)',
    border_color: '#cbcbcb',
    border_mode: 'border',
    border_position: 'all',
    border_width: 1,
    border_radius: 8,
    shadow: 2,
    sizing: 0,
    hide_labels: false,
};
/**
 * "Order summary": the product card (image + name + price + quantity stepper). Shown/hidden
 * via its `order_summary` field; `hide_quantity_controls` hides the +/- stepper.
 */
export const productCardConfig = z.object({
    bg_color: cssColor,
    border_color: cssColor,
    border_width: z.number().int().min(0).max(20),
    border_radius: z.number().int().min(0).max(40),
    hide_quantity_controls: z.boolean(),
    qc_bg_color: cssColor,
    qc_border_color: cssColor,
    qc_border_width: z.number().int().min(0).max(20),
    qc_border_radius: z.number().int().min(0).max(40),
});
export const defaultProductCard = {
    bg_color: 'transparent',
    border_color: 'rgba(0,0,0,.14)',
    border_width: 1,
    border_radius: 10,
    hide_quantity_controls: false,
    qc_bg_color: 'transparent',
    qc_border_color: 'rgba(0,0,0,.25)',
    qc_border_width: 1,
    qc_border_radius: 6,
};
/**
 * "Totals summary": the Subtotal / Shipping / Total box. Carries the row titles, an
 * optional taxes message, box styling and the divider line between Total and the rest.
 */
export const totalsStyleConfig = z.object({
    subtotal_title: z.string().min(1).max(60),
    shipping_title: z.string().min(1).max(60),
    total_title: z.string().min(1).max(60),
    show_taxes: z.boolean(),
    taxes_message: z.string().max(160).default(''),
    bg_color: cssColor,
    text_color: cssColor,
    border_color: cssColor,
    border_width: z.number().int().min(0).max(20),
    border_radius: z.number().int().min(0).max(40),
    divider_color: cssColor,
    divider_width: z.number().int().min(0).max(20),
});
export const defaultTotalsStyle = {
    subtotal_title: 'Subtotal',
    shipping_title: 'Envio',
    total_title: 'Total',
    show_taxes: false,
    taxes_message: 'Impuestos incluidos',
    bg_color: '#e2e3e5',
    text_color: '#1a1a1a',
    border_color: 'rgba(0,0,0,.2)',
    border_width: 0,
    border_radius: 8,
    divider_color: 'rgba(0,0,0,.2)',
    divider_width: 1,
};
export const summaryConfig = z.object({
    /** Text shown as the shipping value (in the totals box and for a zero-price rate) when the
     *  shipping cost is 0, e.g. "Gratis" / "Free". */
    shipping_label: z.string().min(1).max(60).default('Gratis'),
});
/**
 * The "discount code" block: the coupon input + Apply button shown on the form, plus the
 * discount line label in the totals box and the buyer-facing error copy. Carries its own
 * input/button styling (independent of the global field styles) and the "one discount per
 * order" behavior toggle.
 */
export const discountConfig = z.object({
    /**
     * 🚨 RETIRED as a setting (2026-08-17) — the rule it governs is now unconditional.
     *
     * The Designer labelled it "Limit to 1 discount code per order", which the form has never let a
     * buyer break: the block has one input, and applying a code disables it until it is removed. So
     * the checkbox read as redundant, which is why it was pulled. What it actually gated was
     * narrower and unstated — whether a coupon could stack on top of an active QUANTITY-OFFER
     * discount — and `form.ts` now blocks that for everyone rather than reading this flag. Leaving
     * the read would have stranded any merchant whose stored value is `false`: no control to see
     * it, no way to change it.
     *
     * The FIELD stays: existing configs carry it, and it costs nothing. Restore a checkbox here
     * only with a label that says what it does.
     */
    limit_one: z.boolean(),
    /** Label of the discount line in the totals box (shown once a coupon is applied). */
    line_text: z.string().min(1).max(60),
    /** Placeholder text in the coupon input. */
    field_label: z.string().min(1).max(60),
    /** Apply button caption. */
    apply_text: z.string().min(1).max(30),
    button_bg_color: cssColor,
    button_text_color: cssColor,
    button_border_radius: z.number().int().min(0).max(40),
    button_border_width: z.number().int().min(0).max(20),
    button_border_color: cssColor,
    input_bg_color: cssColor,
    input_text_color: cssColor,
    input_border_color: cssColor,
    /** Color of the placeholder (the `field_label` hint) shown in the coupon input.
     *  `.default()` so configs saved before this key existed still parse. */
    input_placeholder_color: cssColor.default('rgba(0,0,0,.45)'),
    input_border_radius: z.number().int().min(0).max(40),
    input_border_width: z.number().int().min(0).max(20),
    /** Shown when a typed code is empty or rejected by the backend. Authoritative: the buyer
     *  sees this merchant-editable text, not the backend's internal reason string. */
    invalid_error: z.string().min(1).max(160),
    /** Shown when a coupon is refused on top of a quantity-offer discount. Still rendered, but no
     *  longer merchant-editable — its Designer field went with `limit_one`'s checkbox, since a
     *  message for a rule you cannot turn off is a setting for nothing. Kept in the config rather
     *  than hardcoded so a merchant who already customised the wording keeps it. */
    limit_error: z.string().min(1).max(160),
});
export const defaultDiscount = {
    limit_one: true,
    line_text: 'Descuento',
    field_label: 'Codigo de descuento',
    apply_text: 'Aplicar',
    button_bg_color: '#000000',
    button_text_color: '#ffffff',
    button_border_radius: 8,
    button_border_width: 0,
    button_border_color: '#000000',
    input_bg_color: 'rgba(36, 88, 210, 0.09)',
    input_text_color: '#000000',
    input_border_color: '#cccccc',
    input_placeholder_color: 'rgba(0,0,0,.45)',
    input_border_radius: 8,
    input_border_width: 1,
    invalid_error: 'Ingresa un codigo de descuento valido.',
    limit_error: 'Solo se permite 1 descuento por pedido.',
};
/**
 * The "shipping method" block: a labeled section with a single (selected) shipping
 * option card, mirroring the Releasit layout. Single-option today; the array shape can
 * grow later without reshaping the payload.
 */
export const shippingMethodConfig = z.object({
    title: z.string().min(1).max(120),
    option_label: z.string().min(1).max(120),
    price_label: z.string().min(1).max(60),
    // Rate card styling. `active_border_color` is the border of the selected rate. Per-field
    // defaults so configs saved with the older (text-only) shipping_method shape backfill these
    // (the whole-key default can't fire when the object already exists).
    bg_color: cssColor.default('#ffffff'),
    text_color: cssColor.default('#000000'),
    border_color: cssColor.default('#e8e8eb'),
    active_border_color: cssColor.default('#ca441e'),
    border_width: z.number().int().min(0).max(20).default(1),
    border_radius: z.number().int().min(0).max(40).default(8),
});
export const defaultShippingMethod = {
    title: 'Metodo de envio',
    option_label: 'Envio gratis',
    price_label: 'Gratis',
    bg_color: '#ffffff',
    text_color: '#000000',
    border_color: '#e8e8eb',
    active_border_color: '#ca441e',
    border_width: 1,
    border_radius: 8,
};
/**
 * Shipping rates. The merchant defines a list of named, priced rates on the Shipping Rates
 * page; the form renders the eligible ones as selectable options and the chosen rate's price
 * is added to the order total + sent as the order's shipping cost. A rate with no conditions
 * (and no country gate) always shows. Weight conditions are intentionally absent — the
 * storefront has no weight data to evaluate them against.
 */
export const shippingConditionType = z.enum([
    'order_total_gte',
    'order_total_lt',
    'quantity_gte',
    'quantity_lt',
    'cart_contains_product',
    'cart_not_contains_product',
]);
/** Condition types whose `value` is a numeric threshold (vs. a product reference). */
export const numericConditionTypes = [
    'order_total_gte',
    'order_total_lt',
    'quantity_gte',
    'quantity_lt',
];
/** One product a `cart_contains_product` / `cart_not_contains_product` condition targets. */
export const shippingConditionProduct = z.object({
    id: z.number().int().positive(),
    /** Captured for display in the admin; the storefront never reads it. */
    name: z.string().max(240).default(''),
});
export const shippingCondition = z.object({
    type: shippingConditionType,
    /** Numeric threshold for order_total_* / quantity_* conditions. */
    value: z.number().min(0).max(100_000_000).default(0),
    /**
     * Target products for cart_contains_product / cart_not_contains_product, evaluated ANY-OF:
     * "contains" passes when the cart holds at least one of them, "does not contain" when it holds
     * none. Several conditions on a rate are ANDed, so a list is the only way to express "or".
     *
     * `.optional()` rather than `.default([])` on purpose: the key then stays absent on the numeric
     * condition types that never use it, and `ShippingCondition` keeps being constructible from a
     * bare `{ type, value }` literal — which is how the conditions are written across the repo.
     */
    products: z.array(shippingConditionProduct).max(200).optional(),
    /**
     * 🚨 LEGACY single-product form, still read. Conditions saved before `products` existed carry
     * only these two, and there is no migration pass: `conditionProducts()` in `shipping.logic.ts`
     * falls back to them, so an old config keeps working in both renderers and the backend without
     * being rewritten. The admin writes `products` and clears these on the next edit.
     */
    product_id: z.number().int().positive().optional(),
    /** Product name, captured for display in the admin. Legacy — see `product_id`. */
    product_name: z.string().max(240).optional(),
});
export const shippingRate = z
    .object({
    id: z.string().min(1).max(64),
    name: z.string().min(1).max(120),
    description: z.string().max(240).default(''),
    price: z.number().min(0).max(1_000_000).default(0),
    conditions: z.array(shippingCondition).default([]),
    /**
     * When true, the rate only shows for buyers in `countries`.
     *
     * 🚨 INERT since 2026-09-08 — country gating is disabled at both ends (the admin control and
     * the `rateEligible` branch that read it are commented out, not deleted). The fields stay in
     * the schema deliberately: a merchant's saved selection has to survive the round trip so
     * switching the feature back on restores it rather than silently losing it. Nothing reads
     * them today. See `config-schema/src/shipping.logic.ts` for the restore checklist.
     */
    country_only: z.boolean().default(false),
    /** ISO 3166-1 alpha-2 country codes (e.g. "CO", "MX"). Inert — see `country_only`. */
    countries: z.array(z.string().min(2).max(2)).default([]),
})
    .passthrough();
/**
 * The wording the retired `address_heading` block shipped with.
 *
 * Kept as a constant — not as a config key — because it is now the CONTENT of the `custom_text`
 * entry the default config ships (`defaults.ts`) and the text `migrateAddressHeadingField` falls
 * back to when converting a stored entry that has no `address_heading.text` of its own.
 */
export const DEFAULT_ADDRESS_HEADING_TEXT = 'Ingresa tu direccion de envio';
/** Styling for the terms consent line. */
export const termsStyle = z.object({
    text_color: cssColor.default('#1a1a1a'),
    link_color: cssColor.default('#00c2cb'),
    font_size: z.number().int().min(8).max(40).default(14),
});
export const defaultTermsStyle = {
    text_color: '#1a1a1a',
    link_color: '#00c2cb',
    font_size: 14,
};
/**
 * Terms consent line. Visibility is governed by the `terms` field entry's `enabled` (like every
 * other field), so there is no `enabled` here. When `url` is set the label renders as a link.
 */
export const termsConfig = z.object({
    url: z.string().url().nullable(),
    label: z.string().min(1).max(240),
    /** Must the buyer accept before submitting? When true (the default) the line shows a required
     *  asterisk and submission is blocked until it's checked. `.default(true)` keeps configs saved
     *  before this key existed mandatory, matching the prior always-enforced behavior. */
    required: z.boolean().default(true),
    /** Shown when the buyer submits without accepting. Empty falls back to `messages.required`,
     *  the same rule every input field's `error_text` follows. Before this existed the string was
     *  hardcoded Spanish in BOTH renderers, so a Portuguese or English storefront announced its one
     *  consent error in a language its buyers might not read. */
    error_text: z.string().max(240).default(''),
    style: termsStyle.default(defaultTermsStyle),
});
/** Styling for the newsletter consent line. */
export const newsletterStyle = z.object({
    text_color: cssColor.default('#1a1a1a'),
    font_size: z.number().int().min(8).max(40).default(14),
});
export const defaultNewsletterStyle = {
    text_color: '#1a1a1a',
    font_size: 14,
};
/** Newsletter opt-in line. Visibility is governed by the `newsletter` field entry's `enabled`. */
export const newsletterConfig = z.object({
    text: z.string().min(1).max(240),
    style: newsletterStyle.default(defaultNewsletterStyle),
});
export const consentConfig = z.object({
    newsletter: newsletterConfig,
    terms: termsConfig,
});
/**
 * Styling for the submit/CTA button. `border_radius` is nullable — `null` inherits the global
 * appearance radius (the historical behavior); a number overrides it.
 */
export const submitStyle = z.object({
    bg_color: cssColor.default('#000000'),
    text_color: cssColor.default('#ffffff'),
    border_radius: z.number().int().min(0).max(100).nullable().default(null),
    font_size: z.number().int().min(8).max(40).default(16),
    // The same styling options the action buttons (WhatsApp/link) carry, so the CTA can match them.
    // Each `.default()` so configs saved before these keys existed backfill cleanly (no regression).
    border_width: z.number().int().min(0).max(20).default(0),
    border_color: cssColor.default('#000000'),
    shadow: z.number().int().min(0).max(40).default(0),
    animation: buttonAnimation.default('none'),
});
export const defaultSubmitStyle = {
    bg_color: '#000000',
    text_color: '#ffffff',
    border_radius: null,
    font_size: 16,
    border_width: 0,
    border_color: '#000000',
    shadow: 0,
    animation: 'none',
};
export const submitConfig = z.object({
    /** Supports the {order_total} token, interpolated at render time. */
    text: z.string().min(1).max(160),
    /** Optional second line shown under the button label (static, like the action buttons).
     *  `.default('')` so configs saved before this key existed backfill cleanly. */
    subtitle: z.string().max(160).default(''),
    style: submitStyle.default(defaultSubmitStyle),
});
/**
 * Validation messages shown by the renderer. `generic_invalid` is the fallback used when
 * a field has no custom `error_text`. Lives in the form config so it ships to the
 * storefront with everything else the renderer needs.
 */
export const messagesConfig = z.object({
    required: z.string().min(1).max(240),
    generic_invalid: z.string().min(1).max(240),
});
export const defaultMessages = {
    required: 'Este campo es obligatorio',
    generic_invalid: 'El valor ingresado no es valido',
};
/**
 * Coverage selection. `id` indexes COVERAGE_CATALOG (see coverage.ts) and the served
 * `/public/coverage/:id` dataset; `null` = no coverage, so province/city/postal stay plain
 * text fields. Only the id rides in the config — the bulk region data is fetched separately.
 *
 * `country` is the country the merchant SELLS in, which scopes the ids on offer. It is set
 * here rather than derived from the store because the Nuvemshop API can't tell us: its
 * `GET /store` `country` is the country the store was created on (a MX-created store selling
 * into Colombia still reports "MX"), and the merchant's enabled-countries setting is not
 * exposed at all. `null` = never chosen, so the admin falls back to the store's API country
 * as an initial guess. It scopes the admin picker AND sets the country on the created order's
 * addresses (the backend falls back to the store's API country only when this is null).
 */
export const coverageConfig = z.object({
    id: z.string().max(32).nullable().default(null),
    country: z.string().length(2).nullable().default(null),
});
export const defaultCoverage = { id: null, country: null };
/** How a quantity tier discounts the line: a percentage, a fixed amount, or nothing. */
export const discountType = z.enum(['percentage', 'fixed', 'none']);
/**
 * One purchasable variant of a product (e.g. "Black / XL"), captured alongside the product
 * in the picker. The offer's variant selectors are populated from this list, so the renderer
 * needs no separate variant lookup. Empty for products that have no options.
 */
export const offerVariant = z.object({
    id: z.number().int().positive(),
    name: z.string().min(1).max(240),
    /** Per-option-type values aligned to the product's `variant_options`, e.g. ["Negro","XL"].
     * Lets the storefront split the picker into one dropdown per option type. Defaulted so offers
     * captured before structured values existed still validate — they fall back to the single
     * combined `name` dropdown. */
    values: z.array(z.string().max(120)).default([]),
});
/** A product targeted by a quantity offer, captured from the product picker. */
export const offerProduct = z.object({
    id: z.number().int().positive(),
    name: z.string().min(1).max(240),
    /** Product thumbnail URL, shown in the offer card and the editor product list. */
    image: z.string().max(2048).optional(),
    /** Effective unit price (promotional price when on sale, else the regular price), in major
     * currency units. Captured from the picker so the editor's live preview shows the real
     * product price instead of a placeholder. Display-only — the storefront always re-prices
     * from the live catalog, so a stale snapshot here never affects what the buyer is charged. */
    price: z.number().nonnegative().optional(),
    /** Selectable variants, offered as per-unit dropdowns on the storefront. New optional
     * key, so offers saved before variants were captured backfill to an empty list. */
    variants: z.array(offerVariant).default([]),
    /** Option-type names for this product, e.g. ["Color","Talla"], aligned to each variant's
     * `values`. Drives the split variant pickers — exactly two options render as two dependent
     * dropdowns; three or more collapse to one combined dropdown. Empty for products with no
     * options, or offers captured before this key existed. */
    variant_options: z.array(z.string().max(120)).default([]),
});
/**
 * A single quantity tier inside an offer, e.g. "Buy 2 items and get 25% off". The
 * renderer prices it from the unit price x `quantity` and the discount, and paints the
 * "plaque" badge from the plaque_* colors. New optional attributes use `.default()`.
 */
export const quantityOfferTier = z.object({
    id: z.string().min(1).max(64),
    /** Supports the {product_name} token, interpolated at render time. */
    title: z.string().min(1).max(185),
    quantity: z.number().int().min(1).max(999),
    /** Whether this tier is selected by default when the form opens. */
    preselect: z.boolean().default(false),
    /** Hide the struck-through comparison (pre-discount) price on the card. */
    hide_compare_price: z.boolean().default(false),
    discount_type: discountType,
    discount_value: z.number().min(0).max(1_000_000).default(0),
    price_color: cssColor,
    plaque_text: z.string().max(120).default(''),
    plaque_text_color: cssColor,
    plaque_bg_color: cssColor,
    /** Optional override thumbnail; falls back to the product image when empty. */
    image_url: z.string().max(2048).default(''),
});
/** Visual template for the offer cards (Sales Booster section 3). */
export const offerTemplate = z.enum(['classic', 'modern', 'vertical']);
/**
 * The vertical template lays its offer cards out as narrow side-by-side columns, so it only
 * stays readable with a handful of them — beyond this the cards get too cramped to use. The
 * admin caps how many offers can be added while vertical is selected, and the renderer only
 * ever draws the first this-many cards, so old configs with more offers still look right.
 */
export const VERTICAL_MAX_TIERS = 3;
/** Max length of the "Best deal" badge caption. It sits beside the savings plaque on one line, so
 *  a long caption would push the price out of the card rather than wrap. */
export const BEST_DEAL_TEXT_MAX = 30;
/**
 * Per-offer design + behaviour. The visual fields (template, colors, radius, price size,
 * hide image) drive the offer cards in the renderer and live preview; the behavioural
 * flags (placement, variant-selector handling, quantity guard) are consumed by the
 * storefront. New keys use `.default()` so offers saved before they existed backfill.
 */
export const offerDesign = z.object({
    /** Render the offers above the product page buy button (popup mode only). */
    display_above_buy_button: z.boolean().default(false),
    template: offerTemplate.default('classic'),
    /** Id of the last-applied color palette, for highlighting it in the editor. */
    palette: z.string().max(32).default('blue'),
    bg_color: cssColor,
    border_color: cssColor,
    /** Offer price font size, in px. */
    prices_size: z.number().int().min(10).max(40).default(15),
    border_radius: z.number().int().min(0).max(40).default(10),
    hide_product_image: z.boolean().default(false),
    hide_variant_numbers: z.boolean().default(false),
    /**
     * 🚨 `use_first_variant` is retired (2026-08-20) — do not reintroduce it.
     *
     * It sat under this toggle and chose, for an offer with no pickers on screen, between the
     * product's FIRST variant and the one the buyer picked on the page. Only the NubeSDK builder
     * ever read it; `form-renderer` always seeded the buyer's variant, so the merchant's setting
     * did nothing on the themes that ship today and would have silently changed the order on the
     * ones that ship next. Every unit now falls back to the buyer's page variant on both bundles
     * (`unitVariantIds` in `@cod/form-tree/pure`).
     *
     * No migration is needed: this object strips unknown keys, so stored offers that still carry
     * `use_first_variant` parse fine and lose it on the next save.
     */
    disable_variant_selectors: z.boolean().default(false),
    /**
     * 🚨 **NO ADMIN UI, deliberately — do not add one back.**
     *
     * "Hide this offer if the buyer already picked a bigger quantity on the product page" is not
     * something Nuvemshop can tell us: the selected quantity is not exposed until the item reaches
     * the cart. So the guard's input is **always 1** — `INITIAL_QUANTITY` on the legacy path (the
     * theme scrape was removed in Phase 1.5) and a literal `1` at `storefront-app/src/main.ts:197` —
     * and `offerAppliesForQuantity` can never return false because of it. The checkbox promised
     * behaviour the platform cannot support, so it was removed from the Designer (2026-08-11).
     *
     * The FIELD stays: existing configs carry it, and dropping it would fail validation on every
     * saved offer for no benefit. The guard in `offers.logic.ts` stays too — it is pure, correct, and
     * costs nothing; it simply has no input that can trigger it. If Nuvemshop ever exposes the
     * product-page quantity, wire that in and the switch becomes meaningful again.
     */
    disable_when_qty_exceeds: z.boolean().default(false),
    /**
     * The "Best deal" badge — a second pill beside the tier's savings plaque, on the **preselected**
     * tier only.
     *
     * 🚨 Offer-level, not per-tier, and that is deliberate. The badge marks whichever tier the
     * merchant preselected, and preselection is exclusive (`applyPreselect` in the admin clears the
     * others), so per-tier fields would be dead data on every tier but one — and moving the
     * preselection would either orphan the badge's text and colours or paint two badges. Here it
     * follows the preselection for free, and one offer can never show two.
     */
    best_deal_show: z.boolean().default(false),
    best_deal_text: z.string().max(BEST_DEAL_TEXT_MAX).default('Mejor oferta'),
    best_deal_text_color: cssColor.default('#ffffff'),
    best_deal_bg_color: cssColor.default('rgba(251,146,60,1)'),
    /** 0–40px. At the top of the range it reads as a pill, like the savings plaque beside it. */
    best_deal_border_radius: z.number().int().min(0).max(40).default(40),
    best_deal_animation: buttonAnimation.default('none'),
});
export const defaultOfferDesign = {
    display_above_buy_button: false,
    template: 'classic',
    palette: 'blue',
    bg_color: 'rgba(217,235,246,1)',
    border_color: 'rgba(0,116,191,1)',
    prices_size: 15,
    border_radius: 10,
    hide_product_image: false,
    hide_variant_numbers: false,
    disable_variant_selectors: false,
    disable_when_qty_exceeds: false,
    best_deal_show: false,
    best_deal_text: 'Mejor oferta',
    best_deal_text_color: '#ffffff',
    best_deal_bg_color: 'rgba(251,146,60,1)',
    best_deal_border_radius: 40,
    best_deal_animation: 'none',
};
/**
 * A quantity offer (Sales Booster). The list carries identity, the on/off state and its
 * position in the array (which IS its priority); the editor adds the targeted `products`,
 * the ordered `tiers` and the `design`. The arrays default to `[]` and `design` defaults
 * so offers saved before they existed backfill, and `.passthrough()` lets still-later keys
 * survive an older parser.
 */
export const quantityOfferConfig = z
    .object({
    id: z.string().min(1).max(64),
    name: z.string().min(1).max(120),
    enabled: z.boolean(),
    products: z.array(offerProduct).default([]),
    tiers: z.array(quantityOfferTier).default([]),
    design: offerDesign.default(defaultOfferDesign),
})
    .passthrough();
/**
 * Reserved space for post-MVP sales boosters. `quantity_offers` is now a typed array;
 * upsells/downsells stay reserved. Passthrough so a future renderer/designer can read
 * richer shapes from old stores.
 */
export const futureConfig = z
    .object({
    quantity_offers: z.array(quantityOfferConfig).default([]),
    upsells: z.array(z.unknown()).default([]),
    downsells: z.array(z.unknown()).default([]),
})
    .passthrough();
/**
 * Ad platforms a tracking pixel can target. `meta` and `google_ads`/`ga4` fire client-side;
 * `meta` (and later `tiktok`) can additionally fire server-side via `server_enabled`. New
 * providers extend the enum; the storefront ignores providers it can't render.
 */
export const pixelProvider = z.enum(['meta', 'tiktok', 'google_ads', 'ga4']);
/**
 * One configured tracking pixel. Provider-centric: a single `pixel_id` powers both the
 * browser pixel and (for Meta/TikTok) the server-side Conversions/Events API, which the
 * shared `event_id` deduplicates. Access tokens are NEVER stored here — they live encrypted
 * in the backend `TrackingSecret` table, keyed by this `id`. `has_token` is a read-only UI
 * hint the admin API sets; `server_enabled` turns on the CAPI fire. New keys use `.default()`
 * (or `.optional()`) so pixels saved before they existed backfill.
 */
export const trackingPixel = z
    .object({
    /** Stable client-generated uuid; the join key to the encrypted token row. */
    id: z.string().min(1).max(64),
    provider: pixelProvider,
    /** The public pixel / measurement / conversion id (fbq id, GA4 measurement id, etc.). */
    pixel_id: z.string().min(1).max(120),
    /** Google Ads conversion label; ignored by other providers. */
    label: z.string().max(120).optional(),
    enabled: z.boolean().default(true),
    /** Meta/TikTok only: also fire the server-side conversion for COD orders. */
    server_enabled: z.boolean().default(false),
    /** Read-only hint that an encrypted access token is stored for this pixel. */
    has_token: z.boolean().default(false),
})
    .passthrough();
/**
 * Tracking-pixels block: a master on/off switch plus the configured pixels. A new top-level
 * config key (per the forward-compat contract); the storefront reads `pixels` to fire client
 * events, and the backend reads it to drive server-side CAPI. Defaults to empty/off so old
 * configs backfill cleanly.
 */
export const trackingConfig = z
    .object({
    enabled: z.boolean().default(false),
    /** When true, the storefront loads no pixel SDK and sets no ad cookies until the buyer
     * grants consent (LGPD/GDPR-style). Default false so existing stores are unaffected. */
    require_consent: z.boolean().default(false),
    pixels: z.array(trackingPixel).default([]),
})
    .passthrough();
export const defaultTracking = {
    enabled: false,
    require_consent: false,
    pixels: [],
};
export const CONFIG_VERSION = 1;
export const formConfig = z
    .object({
    version: z.number().int().positive(),
    enabled: z.boolean(),
    mode: formMode,
    // Store-level setting, mirrored into the served config (backfills to 'product' for
    // configs saved before this existed).
    visibility: formVisibility.default('product'),
    /**
     * SERVER-POPULATED, never merchant-edited. The store's ISO 3166-1 alpha-2 country, mirrored
     * from the `Store` row into the served config by `getPublicConfig`.
     *
     * Existed for country-gated shipping rates (`shippingRate.country_only`), which needed a buyer
     * country to evaluate against. ⚠️ That gate is DISABLED (2026-09-08), so nothing consumes this
     * today — it is still served so the feature can be switched back on without re-plumbing the
     * backend. The legacy storefront read the country from the `LS.country` global; **NubeSDK's
     * `Store` state has no country field**, so the backend has to supply it. See `MIGRATION.md`
     * §4.2 and the restore checklist in `config-schema/src/shipping.logic.ts`.
     *
     * Distinct from `coverage.country`, which is the merchant-chosen country that scopes the
     * coverage dataset and may legitimately differ from where the store was created.
     */
    store_country: z.string().length(2).nullable().default(null),
    /**
     * SERVER-DERIVED, never merchant-edited. Which bundle owns this store — see
     * `storefrontRuntime` above. Defaults to `'legacy'` so a config served by an older backend, or
     * read by a bundle newer than the backend, keeps the bundle that works today running.
     */
    storefront_runtime: storefrontRuntime.default('legacy'),
    // Product-page order scope (Settings toggle). Backfills to 'product_only' (prior behavior).
    cart_mode: cartMode.default('product_only'),
    // New top-level keys use `.default` so configs saved before they existed backfill.
    header: headerConfig.default(defaultHeader),
    button: buttonConfig,
    appearance: appearanceConfig,
    fields_style: fieldsStyleConfig.default(defaultFieldsStyle),
    fields: z.array(fieldConfig).min(1),
    summary: summaryConfig,
    discount: discountConfig.default(defaultDiscount),
    product_card: productCardConfig.default(defaultProductCard),
    totals_style: totalsStyleConfig.default(defaultTotalsStyle),
    shipping_method: shippingMethodConfig.default(defaultShippingMethod),
    // Merchant-defined selectable shipping rates. Backfills to [] for older configs.
    shipping_rates: z.array(shippingRate).default([]),
    // NOTE: no `address_heading` key — the block is retired (see `fieldType` above). A stored
    // config that still carries one is stripped by zod (unknown keys are dropped, not rejected)
    // AFTER `migrateAddressHeadingField` has read its text into a `custom_text` field.
    coverage: coverageConfig.default(defaultCoverage),
    consent: consentConfig,
    submit: submitConfig,
    messages: messagesConfig.default(defaultMessages),
    tracking: trackingConfig.default(defaultTracking),
    thank_you: thankYouConfig.default(defaultThankYou),
    _future: futureConfig,
})
    .passthrough()
    .superRefine((cfg, ctx) => {
    // Field keys must be unique so the order payload is unambiguous.
    const seen = new Set();
    cfg.fields.forEach((f, i) => {
        if (seen.has(f.key)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Clave de campo duplicada: ${f.key}`,
                path: ['fields', i, 'key'],
            });
        }
        seen.add(f.key);
    });
    // The `terms` field entry governs whether the consent checkbox shows; when it does, the
    // renderer links the label if a URL is set and renders plain text otherwise. No hard
    // requirement here, so the out-of-box default config stays saveable.
});
/**
 * Public-facing slice served to the storefront. Today it equals the full config minus
 * nothing secret (the config carries no secrets), but keeping a separate parse point
 * lets us strip internal-only keys later without touching callers.
 */
export const publicFormConfig = formConfig;
//# sourceMappingURL=schema.js.map